import {neverland as $, html, useState, useEffect} from 'neverland';
import QRCode from 'qrcode/lib/browser.js';
import { setupMFA, verifyMFASetup, extractGroupsFromToken, hasIdToken } from '../lib/cognito.js';
import { resolveLoginDecision } from '../lib/clusterAccess.js';
import { sendApprovalNotification } from '../lib/approval.js';

const MFASetupPage = $(function(auth, setAuth, theme) {
  const [step, setStep] = useState('intro'); // 'intro' | 'scan'
  const [setupState, setSetupState] = useState(null); // { secretCode, session, username }
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function getLogo() {
    if (theme && theme.logo && theme.logo !== '') {
      return '/static/img/logos-large/' + theme.logo;
    }
    return '/static/img/transparent-placeholder.png';
  }

  // Brand-neutral issuer/label shown in the user's authenticator app. Kept
  // generic (not "Resilient Youth") because the portal is white-labelled — a
  // school/organisation brand user shouldn't see the RYA name in their app.
  // Note: this is written into the app entry at enrolment, so changing it only
  // affects NEW enrolments; existing users keep their current label until they
  // re-enrol (new phone / MFA reset).
  const appName = 'Reporting Portal';
  const account = auth.userEmail || '';

  // Render the QR code CLIENT-SIDE from the otpauth URI. The TOTP secret never
  // leaves the browser (previously it was sent to a third-party QR service).
  useEffect(() => {
    if (step === 'scan' && setupState && setupState.secretCode) {
      const otpauth = `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(account)}`
        + `?secret=${setupState.secretCode}&issuer=${encodeURIComponent(appName)}`;
      QRCode.toDataURL(otpauth, { width: 200, margin: 1 })
        .then(url => setQrDataUrl(url))
        .catch(() => setQrDataUrl(''));
    }
  }, [step, setupState]);

  async function beginSetup() {
    setError('');
    setLoading(true);
    try {
      const result = await setupMFA(auth.mfaChallenge);
      setSetupState(result);
      setStep('scan');
    } catch (err) {
      setError('Could not start authenticator setup. Please go back and try signing in again.');
    }
    setLoading(false);
  }

  async function verifyAndFinish() {
    const code = document.getElementById('totp-code').value.trim();
    if (!code || code.length !== 6) {
      setError('Enter the 6-digit code shown in your authenticator app.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await verifyMFASetup(setupState, code);
      const groups = extractGroupsFromToken(result);

      // reg-mfa-notify fix: if this enrolment is the tail of a REGISTRATION
      // (RegisterPage stashed registrationEntry + registrationClientGroupId into
      // auth), send the approval notification NOW — this is the first point where
      // a valid Cognito session/ID token exists (verifyMFASetup ->
      // handleAuthResponse -> auth.setSession on the cognito singleton), so
      // database.js can attach X-Id-Token for hop 1. Gated on the stash's
      // PRESENCE, not the gate decision, so an ORDINARY login never sends.
      // Fire-and-forget: the helper swallows its own errors and must not block.
      const isRegistration = !!(auth.registrationEntry && auth.registrationClientGroupId);
      if (isRegistration) {
        sendApprovalNotification(auth, auth.registrationEntry, auth.registrationClientGroupId);
      }

      // Apply the shared login-completion gate before entering the portal.
      // First-time enrolment is usually the first (already approved) registrant,
      // so the common case is 'login'; the gate just stops a pending user who
      // somehow enrols from slipping through.
      const decision = await resolveLoginDecision({ email: auth.userEmail, groups, userData: auth.userData, db: auth.db, isTokenReady: hasIdToken });

      if (decision.action === 'revoked') {
        setError(decision.message);
        setLoading(false);
        return;
      } else if (decision.action === 'register') {
        setAuth(prev => ({
          ...prev,
          mfaPage: 'ConfirmOrgAccess',
          mfaChallenge: null,
          userPassword: null,
          cognitoGroups: groups,
          registrationEntry: null,
          registrationClientGroupId: null
        }));
        setLoading(false);
        return;
      } else if (decision.action === 'pending') {
        setAuth(prev => ({
          ...prev,
          mfaPage: 'PendingApproval',
          mfaChallenge: null,
          userPassword: null,
          cognitoGroups: groups,
          registrationEntry: null,
          registrationClientGroupId: null
        }));
        setLoading(false);
        return;
      }

      setAuth(prev => ({
        ...prev,
        mfaPage: null,
        mfaVerified: true,
        clusterAccess: decision.clusterAccess,
        mfaChallenge: null,
        userPassword: null,
        cognitoGroups: groups,
        registrationEntry: null,
        registrationClientGroupId: null
      }));
    } catch (err) {
      setError('That code was not correct. Make sure you entered the current code from your app (it changes every 30 seconds).');
    }
    setLoading(false);
  }

  function handleKeyPress(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      verifyAndFinish();
    }
  }

  function copySecret() {
    if (setupState && setupState.secretCode && navigator.clipboard) {
      navigator.clipboard.writeText(setupState.secretCode).catch(() => {});
    }
  }

  function goBack() {
    setAuth(prev => ({ ...prev, mfaPage: null, mfaChallenge: null, userData: null }));
  }

  return html`
    <div class="centre-box login">
      <img src="${getLogo()}" class="squiggle logo-large" />

      <div id="login">
        ${step === 'intro' ? html`
          <h2 style="margin-bottom: 10px; font-size: 1.2em;">Set up secure sign-in</h2>
          <p style="margin-bottom: 12px; font-size: 1em; opacity: 0.85;">
            To keep your account secure, you'll confirm each login with a free authenticator app on your phone. This is a one-time setup and only takes a minute.
          </p>

          <ol class="mfa-steps">
            <li><strong>Install an authenticator app</strong> on your phone if you don't have one. Any of these work:
              <div class="mfa-app-links">
                <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noopener">Google Authenticator (iPhone)</a>
                <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener">Google Authenticator (Android)</a>
                <a href="https://www.microsoft.com/en-us/security/mobile-authenticator-app" target="_blank" rel="noopener">Microsoft Authenticator</a>
              </div>
            </li>
            <li><strong>Open the app</strong> and tap the <em>+</em> (add) button to add a new account.</li>
            <li><strong>Choose "Scan a QR code"</strong> — you'll scan the code on the next screen.</li>
          </ol>

          <button id="login-submit" onclick=${beginSetup} disabled=${loading}>
            ${loading ? 'Preparing...' : html`<i class="fal fa-qrcode"></i>&ensp;I'VE GOT THE APP, SHOW ME THE CODE`}
          </button>
        ` : html`
          <h2 style="margin-bottom: 10px; font-size: 1.2em;">Scan this code</h2>
          <p style="margin-bottom: 10px; font-size: 1em; opacity: 0.85;">
            In your authenticator app, tap <em>+</em> then <em>Scan a QR code</em>, and point your phone at this:
          </p>

          ${qrDataUrl
            ? html`<img src="${qrDataUrl}" alt="QR code for authenticator setup" style="display:block;margin:0 auto 10px;width:200px;height:200px;" />`
            : html`<p style="font-size:1em;">Generating code...</p>`}

          <details style="margin-bottom:12px;font-size:0.95em;">
            <summary style="cursor:pointer;">Can't scan? Enter the key manually</summary>
            <p style="margin:8px 0 4px;">Type this key into your app instead of scanning:</p>
            <code style="font-size:1.15em;letter-spacing:1px;word-break:break-all;display:block;padding:8px;background:#f4f4f4;border-radius:4px;">${setupState ? setupState.secretCode : ''}</code>
            <button type="button" class="back-link" style="margin-top:4px;" onclick=${copySecret}>Copy key</button>
          </details>

          <p style="font-size: 1em; margin-bottom: 6px;">Then enter the 6-digit code your app shows:</p>
          <input type="text" id="totp-code" class="login-field" maxlength="6" inputmode="numeric" placeholder="000000" onkeyup=${handleKeyPress} autofocus />

          <button id="login-submit" onclick=${verifyAndFinish} disabled=${loading}>
            ${loading ? 'Verifying...' : html`<i class="fal fa-check"></i>&ensp;VERIFY & FINISH`}
          </button>
        `}

        <button class="back-link" onclick=${goBack}>
          &larr; Back to Login
        </button>

        ${error ? html`<div class="error"><strong>ERROR</strong><p>${error}</p></div>` : ''}
      </div>
    </div>
  `;
});

export default MFASetupPage;
